import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX,
  FiMail,
  FiPhone,
  FiUser,
  FiBriefcase,
  FiSend,
  FiCheckCircle,
} from 'react-icons/fi';

const ContactFormModal = ({ isOpen, onClose, productName = null }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    industry: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const industries = [
    'Oil & Petroleum',
    'Heavy Machinery & Engineering',
    'Agriculture & Horticulture',
    'Marine & Shipping',
    'Mining (Surface & Underground)',
    'Power Generation & Utilities',
    'Automotive Service & Repair',
    'Construction & Infrastructure',
    'Manufacturing & Fabrication',
    'Defense & Railways',
    'Other',
  ];

  const subjects = [
    'Product Quote',
    'Technical Support',
    'Partnership Inquiry',
    'Bulk Order',
    'Custom Solution',
    'Other',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const sendEmail = async ({ to, subject, html }) => {
    const form = new FormData();
    form.append('organizationId', 'SamHealthNew');
    form.append('to', to);
    form.append('subject', subject);
    form.append('html', html);
    
    try {
      const response = await fetch('https://imageupload.scontiapp.com/api/send-one/', {
        method: 'POST',
        body: form
      });
      return response.ok;
    } catch (err) {
      console.error('Email send error:', err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Format email HTML body
    const emailHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff; }
            .header { background: linear-gradient(135deg, #00875a 0%, #0066cc 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
            .alert-badge { background: #ff6b35; color: white; padding: 8px 16px; border-radius: 20px; font-size: 12px; font-weight: bold; display: inline-block; margin-bottom: 10px; }
            .product-badge { background: #00875a; color: white; padding: 12px 20px; border-radius: 8px; font-size: 16px; font-weight: bold; display: inline-block; margin: 15px 0; }
            .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
            .field { margin-bottom: 20px; }
            .label { font-weight: bold; color: #00875a; margin-bottom: 5px; font-size: 14px; text-transform: uppercase; }
            .value { background: white; padding: 12px; border-radius: 5px; border-left: 4px solid #00875a; font-size: 16px; }
            .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; border-top: 2px solid #e0e0e0; margin-top: 20px; }
            .highlight { color: #0066cc; font-weight: bold; }
            .urgent { background: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin-bottom: 20px; border-radius: 5px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="alert-badge">🌐 NEW WEBSITE INQUIRY</div>
              <h2 style="margin: 10px 0;">${productName ? 'Product Inquiry' : 'Contact Form Submission'}</h2>
              <p style="margin: 5px 0; font-size: 14px;">VP Chem Technologies Website</p>
              ${productName ? `<div class="product-badge">📦 Product: ${productName}</div>` : ''}
            </div>
            <div class="content">
              <div class="urgent">
                <strong>⚡ Action Required:</strong> A potential customer has ${productName ? `requested information about ${productName}` : 'submitted an inquiry'} through your website. Please respond within 24 hours for the best customer experience.
              </div>
              
              <h3 style="color: #333; margin-bottom: 20px; border-bottom: 2px solid #00875a; padding-bottom: 10px;">
                📋 Customer Details
              </h3>
              
              <div class="field">
                <div class="label">👤 Full Name:</div>
                <div class="value">${formData.fullName}</div>
              </div>
              <div class="field">
                <div class="label">🏢 Company Name:</div>
                <div class="value">${formData.companyName}</div>
              </div>
              <div class="field">
                <div class="label">📧 Email Address:</div>
                <div class="value"><a href="mailto:${formData.email}" style="color: #0066cc; text-decoration: none;">${formData.email}</a></div>
              </div>
              <div class="field">
                <div class="label">📱 Phone Number:</div>
                <div class="value"><a href="tel:${formData.phone}" style="color: #0066cc; text-decoration: none;">${formData.phone}</a></div>
              </div>
              <div class="field">
                <div class="label">🏭 Industry:</div>
                <div class="value">${formData.industry || 'Not specified'}</div>
              </div>
              <div class="field">
                <div class="label">📌 Subject/Inquiry Type:</div>
                <div class="value"><strong>${formData.subject || 'General Inquiry'}</strong></div>
              </div>
              ${productName ? `
              <div class="field">
                <div class="label">🎯 Product Inquiry:</div>
                <div class="value" style="background: #e8f5e9; border-left: 4px solid #4caf50;"><strong>${productName}</strong></div>
              </div>
              ` : ''}
              
              <h3 style="color: #333; margin: 30px 0 20px 0; border-bottom: 2px solid #00875a; padding-bottom: 10px;">
                💬 Customer Message
              </h3>
              <div class="field">
                <div class="value" style="min-height: 80px; white-space: pre-wrap;">${formData.message}</div>
              </div>
              
              <h3 style="color: #333; margin: 30px 0 20px 0; border-bottom: 2px solid #00875a; padding-bottom: 10px;">
                ⏰ Submission Details
              </h3>
              <div class="field">
                <div class="label">📅 Date & Time:</div>
                <div class="value">${new Date().toLocaleString('en-IN', { 
                  timeZone: 'Asia/Kolkata',
                  dateStyle: 'full',
                  timeStyle: 'long'
                })}</div>
              </div>
              <div class="field">
                <div class="label">🌐 Source:</div>
                <div class="value">Website ${productName ? `(${productName} Product Page)` : 'Contact Form'}</div>
              </div>
            </div>
            <div class="footer">
              <p><strong>⚠️ This is an automated notification from VP Chem Technologies website.</strong></p>
              <p>Please respond to the customer directly at: <span class="highlight">${formData.email}</span></p>
              <p style="margin-top: 15px; font-size: 11px; color: #999;">
                This email was generated automatically when a visitor submitted ${productName ? `a product inquiry for ${productName}` : 'the contact form'} on your website.<br/>
                © ${new Date().getFullYear()} VP Chem Technologies LLP. All rights reserved.
              </p>
            </div>
          </div>
        </body>
      </html>
    `;

    // Send email
    const emailSent = await sendEmail({
      to: 'vpchemtechnologies19@gmail.com',
      subject: `🌐 NEW ${productName ? `${productName} INQUIRY` : 'WEBSITE INQUIRY'} | ${formData.subject || 'General Inquiry'} | ${formData.fullName} | ${formData.companyName}`,
      html: emailHtml
    });

    setIsSubmitting(false);

    if (emailSent) {
      setIsSuccess(true);
      
      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setFormData({
          fullName: '',
          companyName: '',
          email: '',
          phone: '',
          industry: '',
          subject: '',
          message: '',
        });
        setIsSuccess(false);
        onClose();
      }, 3000);
    } else {
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-vp-grey-200 hover:bg-vp-grey-300 text-vp-grey-700 hover:text-vp-grey-900 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>

            {/* Modal Content */}
            <div className="p-8 md:p-10">
              {/* Header */}
              <div className="text-center mb-8">
                <motion.div
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-vp-green-100 to-vp-blue-100 px-6 py-3 rounded-full text-vp-green-700 mb-6 border border-vp-green-400/30"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FiMail className="text-vp-green-600 text-2xl" />
                  <span className="font-semibold">{productName ? `Inquire About ${productName}` : 'Get in Touch'}</span>
                </motion.div>

                <h2 className="text-3xl md:text-4xl font-bold text-vp-grey-900 mb-4">
                  {productName ? 'Request a Quote' : 'Contact Our Experts'}
                </h2>
                <p className="text-lg text-vp-grey-700">
                  Fill out the form below and we'll get back to you within one business day.
                </p>
              </div>

              {/* Success State */}
              {isSuccess ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="text-center py-12"
                >
                  <motion.div
                    className="w-24 h-24 bg-gradient-to-br from-vp-green-500 to-vp-blue-500 rounded-full flex items-center justify-center mx-auto mb-6"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <FiCheckCircle className="w-12 h-12 text-white" />
                  </motion.div>
                  <h3 className="text-3xl font-bold text-vp-grey-900 mb-4">Thank You!</h3>
                  <p className="text-xl text-vp-grey-700">
                    Your inquiry has been submitted successfully. We'll get back to you soon.
                  </p>
                </motion.div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiUser className="text-vp-green-600" />
                        <span>Full Name *</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 placeholder-vp-grey-500 focus:border-vp-green-500 focus:ring-2 focus:ring-vp-green-500/50 transition-all outline-none"
                        placeholder="Enter your full name"
                      />
                    </div>

                    {/* Company Name */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiBriefcase className="text-vp-blue-600" />
                        <span>Company Name *</span>
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 placeholder-vp-grey-500 focus:border-vp-blue-500 focus:ring-2 focus:ring-vp-blue-500/50 transition-all outline-none"
                        placeholder="Enter your company name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiMail className="text-vp-green-600" />
                        <span>Email Address *</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 placeholder-vp-grey-500 focus:border-vp-green-500 focus:ring-2 focus:ring-vp-green-500/50 transition-all outline-none"
                        placeholder="your.email@company.com"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiPhone className="text-vp-blue-600" />
                        <span>Phone Number *</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 placeholder-vp-grey-500 focus:border-vp-blue-500 focus:ring-2 focus:ring-vp-blue-500/50 transition-all outline-none"
                        placeholder="+91 XXXXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Industry */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiBriefcase className="text-vp-green-600" />
                        <span>Industry</span>
                      </label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 focus:border-vp-green-500 focus:ring-2 focus:ring-vp-green-500/50 transition-all outline-none"
                      >
                        <option value="">Select your industry</option>
                        {industries.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                        <FiMail className="text-vp-blue-600" />
                        <span>Subject</span>
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 focus:border-vp-blue-500 focus:ring-2 focus:ring-vp-blue-500/50 transition-all outline-none"
                      >
                        <option value="">Select subject</option>
                        {subjects.map((subject) => (
                          <option key={subject} value={subject}>
                            {subject}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-bold text-vp-grey-900 mb-2 flex items-center space-x-2">
                      <FiMail className="text-vp-green-600" />
                      <span>Your Message *</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 bg-vp-cream-50 border-2 border-vp-grey-300 rounded-xl text-vp-grey-900 placeholder-vp-grey-500 focus:border-vp-green-500 focus:ring-2 focus:ring-vp-green-500/50 transition-all outline-none resize-none"
                      placeholder={productName ? `Tell us about your requirements for ${productName}...` : "Tell us how we can help you..."}
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full px-8 py-4 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-vp-green-500/50 transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <FiSend className="text-xl" />
                        <span>Submit Inquiry</span>
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ContactFormModal;
